import { SubmenuDivHandler } from './SubmenuDivHandler.js';
import { SmoothRotate } from '../smooth-rotate.js';
import { SnapRotate } from '../snap-rotation.js';
import { applyComponentStyles } from '../utils/shared-styles.js';
import { UserSettingsStore } from '../utils/user-settings-store.js';
import Logger from '../utils/logger.js';
import {
    createSettingsMenuSchema,
    formatRotationSpeedLabel,
    SETTINGS_MENU_LABELS,
} from './schemas/settings-menu-schema.js';
import { isNodeVisibleOnPlatform, findMenuNodeById } from './MenuSchema.js';

const MODULE = 'SettingsSubmenu';

type RotationMode = 'smooth' | 'snap';
type AudioHintMode = 'auto' | 'speech' | 'music' | 'none';
type NoiseSuppressionMode = 'default' | 'mild' | 'aggressive';
type AudioRoutingMode = 'premix' | 'split';

interface RuntimeAudioTuningSettings {
    audioRoutingMode: AudioRoutingMode;
    echoCancellation: boolean;
    noiseSuppression: boolean;
    noiseSuppressionMode: NoiseSuppressionMode;
    broadcastDisableProcessing: boolean;
    autoGainControl: boolean;
    enableSenderBitrateCap: boolean;
    speechMaxBitrate: number;
    musicMaxBitrate: number;
    contentHintMode: AudioHintMode;
}

interface AudioTuningApi {
    get(): RuntimeAudioTuningSettings;
    set(partial: Partial<RuntimeAudioTuningSettings>): RuntimeAudioTuningSettings;
}

const CSS = `
        .mm-settings-section {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
            align-items: stretch;
            margin-top: 0.5rem;
        }

        .mm-settings-card {
            border: 1px solid #00d9ff;
            border-radius: 10px;
            padding: 0.65rem;
            background: rgba(10, 15, 30, 0.45);
            text-align: left;
        }

        .mm-settings-card-title {
            color: #00ffff;
            font-weight: 700;
            margin-bottom: 0.6rem;
            font-size: clamp(0.85rem, 2.3vw, 1rem);
            text-transform: uppercase;
            letter-spacing: 0.05em;
        }

        .mm-settings-row {
            display: flex;
            flex-direction: column;
            align-items: stretch;
            gap: 0.35rem;
            text-align: left;
        }

        .mm-settings-label {
            color: #b8e3f0;
            font-size: clamp(0.8rem, 2.2vw, 0.95rem);
            font-weight: 600;
        }

        .mm-settings-check-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 0.6rem;
            padding: 0.15rem 0;
        }

        .mm-settings-checkbox {
            width: 18px;
            height: 18px;
            accent-color: #00d9ff;
        }

        .mm-settings-slider-row {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            flex-wrap: wrap;
        }

        .mm-settings-slider {
            width: min(300px, 90%);
            accent-color: #00d9ff;
            flex: 1;
            min-width: 140px;
        }

        .mm-settings-value {
            min-width: 5rem;
            color: #00ffff;
            font-weight: 700;
            text-align: right;
        }

        .mm-settings-debug-box {
            margin-top: 0.75rem;
            border: 1px solid #00d9ff;
            border-radius: 8px;
            padding: 0.5rem;
            background: rgba(10, 15, 30, 0.5);
            text-align: left;
        }

        .mm-settings-debug-title {
            color: #00ffff;
            font-weight: 700;
            margin-bottom: 0.35rem;
            font-size: clamp(0.75rem, 2.1vw, 0.9rem);
        }

        .mm-settings-debug-text {
            width: 100%;
            min-height: 150px;
            resize: vertical;
            border: 1px solid #00d9ff;
            border-radius: 6px;
            background: rgba(10, 15, 30, 0.85);
            color: #b8e3f0;
            font-size: 0.8rem;
            line-height: 1.35;
            padding: 0.5rem;
            font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        }

        .mm-settings-debug-actions {
            margin-top: 0.4rem;
            display: flex;
            justify-content: flex-end;
        }
`;

const MM_SETTINGS_HTML = `
        <div id="menu-modal-settings" class="menu-modal-content">
            <h2>Settings</h2>
            <div class="mm-settings-section">
                <div class="mm-settings-card">
                    <div class="mm-settings-card-title">Audio Settings</div>

                    <div class="mm-settings-row">
                        <label class="mm-settings-label" for="mm-settings-audio-routing-mode">Audio Routing Mode</label>
                        <select id="mm-settings-audio-routing-mode" class="mm-text-input" onkeydown="event.stopPropagation()">
                            <option value="split">split</option>
                            <option value="premix">premix</option>
                        </select>
                    </div>

                    <div class="mm-settings-row">
                        <label class="mm-settings-label" for="mm-settings-audio-content-hint">Content Hint Mode</label>
                        <select id="mm-settings-audio-content-hint" class="mm-text-input" onkeydown="event.stopPropagation()">
                            <option value="auto">auto</option>
                            <option value="speech">speech</option>
                            <option value="music">music</option>
                            <option value="none">none</option>
                        </select>
                    </div>

                    <div class="mm-settings-row">
                        <label class="mm-settings-label" for="mm-settings-audio-noise-mode">Noise Suppression Mode</label>
                        <select id="mm-settings-audio-noise-mode" class="mm-text-input" onkeydown="event.stopPropagation()">
                            <option value="default">default</option>
                            <option value="mild">mild</option>
                            <option value="aggressive">aggressive</option>
                        </select>
                    </div>

                    <div class="mm-settings-row">
                        <label class="mm-settings-label" for="mm-settings-audio-speech-bitrate">Speech Max Bitrate</label>
                        <input id="mm-settings-audio-speech-bitrate" class="mm-text-input" type="number" min="16000" max="512000" step="1000" onkeydown="event.stopPropagation()" />
                    </div>

                    <div class="mm-settings-row">
                        <label class="mm-settings-label" for="mm-settings-audio-music-bitrate">Music Max Bitrate</label>
                        <input id="mm-settings-audio-music-bitrate" class="mm-text-input" type="number" min="32000" max="512000" step="1000" onkeydown="event.stopPropagation()" />
                    </div>

                    <div class="mm-settings-check-row">
                        <label class="mm-settings-label" for="mm-settings-audio-echo-cancellation">Echo Cancellation</label>
                        <input id="mm-settings-audio-echo-cancellation" class="mm-settings-checkbox" type="checkbox" />
                    </div>

                    <div class="mm-settings-check-row">
                        <label class="mm-settings-label" for="mm-settings-audio-noise-suppression">Noise Suppression</label>
                        <input id="mm-settings-audio-noise-suppression" class="mm-settings-checkbox" type="checkbox" />
                    </div>

                    <div class="mm-settings-check-row">
                        <label class="mm-settings-label" for="mm-settings-audio-broadcast-disable">Broadcast Disable Processing</label>
                        <input id="mm-settings-audio-broadcast-disable" class="mm-settings-checkbox" type="checkbox" />
                    </div>

                    <div class="mm-settings-check-row">
                        <label class="mm-settings-label" for="mm-settings-audio-auto-gain">Auto Gain Control</label>
                        <input id="mm-settings-audio-auto-gain" class="mm-settings-checkbox" type="checkbox" />
                    </div>

                    <div class="mm-settings-check-row">
                        <label class="mm-settings-label" for="mm-settings-audio-bitrate-cap">Enable Sender Bitrate Cap</label>
                        <input id="mm-settings-audio-bitrate-cap" class="mm-settings-checkbox" type="checkbox" />
                    </div>
                </div>

                <div class="mm-settings-card">
                    <div class="mm-settings-card-title">Rotation Settings (VR Mode)</div>
                <div class="mm-settings-row">
                    <label class="mm-settings-label" for="mm-settings-rotation-mode">Rotation Mode</label>
                    <select id="mm-settings-rotation-mode" class="mm-text-input" onkeydown="event.stopPropagation()">
                        <option value="smooth">Smooth</option>
                        <option value="snap">Snap</option>
                    </select>
                </div>

                <div id="mm-settings-smooth-row" class="mm-settings-row">
                    <label class="mm-settings-label" for="mm-settings-smooth-slider">Smooth Rotation Speed (deg/s)</label>
                    <div class="mm-settings-slider-row">
                        <input id="mm-settings-smooth-slider" class="mm-settings-slider" type="range" min="10" max="360" step="5" value="90" />
                        <span id="mm-settings-smooth-value" class="mm-settings-value">90°/s</span>
                    </div>
                </div>

                <div id="mm-settings-snap-row" class="mm-settings-row">
                    <label class="mm-settings-label" for="mm-settings-snap-slider">Snap Rotation Angle (degrees)</label>
                    <div class="mm-settings-slider-row">
                        <input id="mm-settings-snap-slider" class="mm-settings-slider" type="range" min="5" max="180" step="5" value="45" />
                        <span id="mm-settings-snap-value" class="mm-settings-value">45°</span>
                    </div>
                </div>
                </div>

                <div class="mm-settings-debug-box">
                    <div class="mm-settings-debug-title">Persisted User Settings (Debug)</div>
                    <textarea id="mm-settings-debug-text" class="mm-settings-debug-text" readonly></textarea>
                    <div class="mm-settings-debug-actions">
                        <button id="mm-settings-debug-copy" class="mm-submit-button">Copy</button>
                        <button id="mm-settings-debug-refresh" class="mm-submit-button">Refresh</button>
                    </div>
                </div>
            </div>
        </div>
`;

export class SettingsSubmenuDH extends SubmenuDivHandler {
    static TypeName = 'SettingsSubmenuDH';

    private currentMode: RotationMode = 'smooth';

    private getAudioTuningApi(): AudioTuningApi | null {
        const api = (window as any).wecloudAudioTuning as AudioTuningApi | undefined;
        if (!api || typeof api.get !== 'function' || typeof api.set !== 'function') {
            return null;
        }
        return api;
    }

    private applyAudioSettingsToControls(settings: RuntimeAudioTuningSettings): void {
        const audioRoutingMode = document.getElementById('mm-settings-audio-routing-mode') as HTMLSelectElement | null;
        const contentHintMode = document.getElementById('mm-settings-audio-content-hint') as HTMLSelectElement | null;
        const noiseMode = document.getElementById('mm-settings-audio-noise-mode') as HTMLSelectElement | null;
        const speechBitrate = document.getElementById('mm-settings-audio-speech-bitrate') as HTMLInputElement | null;
        const musicBitrate = document.getElementById('mm-settings-audio-music-bitrate') as HTMLInputElement | null;
        const echoCancellation = document.getElementById('mm-settings-audio-echo-cancellation') as HTMLInputElement | null;
        const noiseSuppression = document.getElementById('mm-settings-audio-noise-suppression') as HTMLInputElement | null;
        const broadcastDisable = document.getElementById('mm-settings-audio-broadcast-disable') as HTMLInputElement | null;
        const autoGain = document.getElementById('mm-settings-audio-auto-gain') as HTMLInputElement | null;
        const bitrateCap = document.getElementById('mm-settings-audio-bitrate-cap') as HTMLInputElement | null;

        if (audioRoutingMode) audioRoutingMode.value = settings.audioRoutingMode;
        if (contentHintMode) contentHintMode.value = settings.contentHintMode;
        if (noiseMode) noiseMode.value = settings.noiseSuppressionMode;
        if (speechBitrate) speechBitrate.value = String(settings.speechMaxBitrate);
        if (musicBitrate) musicBitrate.value = String(settings.musicMaxBitrate);
        if (echoCancellation) echoCancellation.checked = settings.echoCancellation;
        if (noiseSuppression) noiseSuppression.checked = settings.noiseSuppression;
        if (broadcastDisable) broadcastDisable.checked = settings.broadcastDisableProcessing;
        if (autoGain) autoGain.checked = settings.autoGainControl;
        if (bitrateCap) bitrateCap.checked = settings.enableSenderBitrateCap;
    }

    private refreshAudioControlsFromApi(): void {
        const api = this.getAudioTuningApi();
        if (!api) {
            return;
        }
        const settings = api.get();
        this.applyAudioSettingsToControls(settings);
    }

    private setAudioControlsEnabled(enabled: boolean): void {
        const controlIds = [
            'mm-settings-audio-routing-mode',
            'mm-settings-audio-content-hint',
            'mm-settings-audio-noise-mode',
            'mm-settings-audio-speech-bitrate',
            'mm-settings-audio-music-bitrate',
            'mm-settings-audio-echo-cancellation',
            'mm-settings-audio-noise-suppression',
            'mm-settings-audio-broadcast-disable',
            'mm-settings-audio-auto-gain',
            'mm-settings-audio-bitrate-cap',
        ];

        for (const id of controlIds) {
            const element = document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null;
            if (element) {
                element.disabled = !enabled;
            }
        }
    }

    private wireAudioSettingsControls(): void {
        const api = this.getAudioTuningApi();
        if (!api) {
            this.setAudioControlsEnabled(false);
            Logger.warn(MODULE, 'wecloudAudioTuning API unavailable; audio controls disabled.');
            return;
        }

        this.setAudioControlsEnabled(true);
        this.refreshAudioControlsFromApi();

        const audioRoutingMode = document.getElementById('mm-settings-audio-routing-mode') as HTMLSelectElement | null;
        const contentHintMode = document.getElementById('mm-settings-audio-content-hint') as HTMLSelectElement | null;
        const noiseMode = document.getElementById('mm-settings-audio-noise-mode') as HTMLSelectElement | null;
        const speechBitrate = document.getElementById('mm-settings-audio-speech-bitrate') as HTMLInputElement | null;
        const musicBitrate = document.getElementById('mm-settings-audio-music-bitrate') as HTMLInputElement | null;
        const echoCancellation = document.getElementById('mm-settings-audio-echo-cancellation') as HTMLInputElement | null;
        const noiseSuppression = document.getElementById('mm-settings-audio-noise-suppression') as HTMLInputElement | null;
        const broadcastDisable = document.getElementById('mm-settings-audio-broadcast-disable') as HTMLInputElement | null;
        const autoGain = document.getElementById('mm-settings-audio-auto-gain') as HTMLInputElement | null;
        const bitrateCap = document.getElementById('mm-settings-audio-bitrate-cap') as HTMLInputElement | null;

        audioRoutingMode?.addEventListener('change', () => {
            api.set({ audioRoutingMode: audioRoutingMode.value as AudioRoutingMode });
            this.refreshAudioControlsFromApi();
            this.updatePersistedSettingsDebugView();
        });

        contentHintMode?.addEventListener('change', () => {
            api.set({ contentHintMode: contentHintMode.value as AudioHintMode });
            this.refreshAudioControlsFromApi();
            this.updatePersistedSettingsDebugView();
        });

        noiseMode?.addEventListener('change', () => {
            api.set({ noiseSuppressionMode: noiseMode.value as NoiseSuppressionMode });
            this.refreshAudioControlsFromApi();
            this.updatePersistedSettingsDebugView();
        });

        speechBitrate?.addEventListener('change', () => {
            const value = Number(speechBitrate.value);
            if (!Number.isFinite(value)) {
                this.refreshAudioControlsFromApi();
                return;
            }
            api.set({ speechMaxBitrate: value });
            this.refreshAudioControlsFromApi();
            this.updatePersistedSettingsDebugView();
        });

        musicBitrate?.addEventListener('change', () => {
            const value = Number(musicBitrate.value);
            if (!Number.isFinite(value)) {
                this.refreshAudioControlsFromApi();
                return;
            }
            api.set({ musicMaxBitrate: value });
            this.refreshAudioControlsFromApi();
            this.updatePersistedSettingsDebugView();
        });

        echoCancellation?.addEventListener('change', () => {
            api.set({ echoCancellation: echoCancellation.checked });
            this.refreshAudioControlsFromApi();
            this.updatePersistedSettingsDebugView();
        });

        noiseSuppression?.addEventListener('change', () => {
            api.set({ noiseSuppression: noiseSuppression.checked });
            this.refreshAudioControlsFromApi();
            this.updatePersistedSettingsDebugView();
        });

        broadcastDisable?.addEventListener('change', () => {
            api.set({ broadcastDisableProcessing: broadcastDisable.checked });
            this.refreshAudioControlsFromApi();
            this.updatePersistedSettingsDebugView();
        });

        autoGain?.addEventListener('change', () => {
            api.set({ autoGainControl: autoGain.checked });
            this.refreshAudioControlsFromApi();
            this.updatePersistedSettingsDebugView();
        });

        bitrateCap?.addEventListener('change', () => {
            api.set({ enableSenderBitrateCap: bitrateCap.checked });
            this.refreshAudioControlsFromApi();
            this.updatePersistedSettingsDebugView();
        });
    }

    constructor() {
        super();
        applyComponentStyles(CSS, 'settings-submenu');
    }

    private findRotationPair(): { smooth: SmoothRotate; snap: SnapRotate } | null {
        for (const smooth of SmoothRotate.instances) {
            for (const snap of SnapRotate.instances) {
                if (snap.object === smooth.object) {
                    return { smooth, snap };
                }
            }
        }

        const firstSmooth = Array.from(SmoothRotate.instances)[0];
        const firstSnap = Array.from(SnapRotate.instances)[0];
        if (firstSmooth && firstSnap) {
            return { smooth: firstSmooth, snap: firstSnap };
        }

        return null;
    }

    private syncModeFromComponents(pair: { smooth: SmoothRotate; snap: SnapRotate }): RotationMode {
        if (pair.snap.active && !pair.smooth.active) {
            return 'snap';
        }
        return 'smooth';
    }

    private applyMode(mode: RotationMode): void {
        const pair = this.findRotationPair();
        if (!pair) {
            Logger.error(MODULE, 'Could not find smooth-rotate and snap-rotate components for settings menu.');
            return;
        }

        // Store must be updated BEFORE calling setControlActive so that if WLE fires
        // start() on first activation the component reads the correct persisted mode.
        this.currentMode = mode;
        UserSettingsStore.setString(UserSettingsStore.Keys.RotationMode, mode);

        if (mode === 'smooth') {
            pair.smooth.setControlActive(true);
            pair.snap.setControlActive(false);
        } else {
            pair.smooth.setControlActive(false);
            pair.snap.setControlActive(true);
        }

        this.updateConditionals();
    }

    private setSmoothValueLabel(value: number): void {
        const smoothValue = document.getElementById('mm-settings-smooth-value') as HTMLSpanElement | null;
        if (smoothValue) {
            smoothValue.innerText = formatRotationSpeedLabel('smooth', value).replace(`${SETTINGS_MENU_LABELS.smoothSpeedPrefix} `, '');
        }
    }

    private setSnapValueLabel(value: number): void {
        const snapValue = document.getElementById('mm-settings-snap-value') as HTMLSpanElement | null;
        if (snapValue) {
            snapValue.innerText = formatRotationSpeedLabel('snap', value).replace(`${SETTINGS_MENU_LABELS.snapAnglePrefix} `, '');
        }
    }

    private applySchemaLabels(containerDiv: HTMLDivElement): void {
        const schema = createSettingsMenuSchema();

        const titleEl = containerDiv.querySelector('h2');
        if (titleEl) {
            titleEl.textContent = schema.title;
        }

        const cardTitles = containerDiv.querySelectorAll('.mm-settings-card-title');
        if (cardTitles[0]) {
            cardTitles[0].textContent = SETTINGS_MENU_LABELS.webAudioCardTitle;
        }
        if (cardTitles[1]) {
            cardTitles[1].textContent = SETTINGS_MENU_LABELS.webRotationCardTitle;
        }

        const rotationModeLabel = containerDiv.querySelector('label[for="mm-settings-rotation-mode"]');
        if (rotationModeLabel) {
            rotationModeLabel.textContent = SETTINGS_MENU_LABELS.webRotationModeLabel;
        }

        const smoothLabel = containerDiv.querySelector('label[for="mm-settings-smooth-slider"]');
        if (smoothLabel) {
            smoothLabel.textContent = SETTINGS_MENU_LABELS.webSmoothLabel;
        }

        const snapLabel = containerDiv.querySelector('label[for="mm-settings-snap-slider"]');
        if (snapLabel) {
            snapLabel.textContent = SETTINGS_MENU_LABELS.webSnapLabel;
        }

        const echoLabel = containerDiv.querySelector('label[for="mm-settings-audio-echo-cancellation"]');
        if (echoLabel) {
            echoLabel.textContent = SETTINGS_MENU_LABELS.webEchoLabel;
        }

        const noiseLabel = containerDiv.querySelector('label[for="mm-settings-audio-noise-suppression"]');
        if (noiseLabel) {
            noiseLabel.textContent = SETTINGS_MENU_LABELS.webNoiseLabel;
        }

        const debugTitle = containerDiv.querySelector('.mm-settings-debug-title');
        if (debugTitle) {
            debugTitle.textContent = SETTINGS_MENU_LABELS.webDebugTitle;
        }

        const debugCopyButton = document.getElementById('mm-settings-debug-copy') as HTMLButtonElement | null;
        if (debugCopyButton) {
            debugCopyButton.textContent = SETTINGS_MENU_LABELS.webDebugCopy;
        }

        const debugRefreshButton = document.getElementById('mm-settings-debug-refresh') as HTMLButtonElement | null;
        if (debugRefreshButton) {
            debugRefreshButton.textContent = SETTINGS_MENU_LABELS.webDebugRefresh;
        }
    }

    /**
     * Hides elements from the schema that are marked as VR-only (platforms: ['vr']).
     * This ensures web users only see content marked for web or unmarked (defaults to visible).
     * Currently hides the entire Rotation Settings card if rotation nodes are VR-only.
     */
    private hideVrOnlyElements(containerDiv: HTMLDivElement): void {
        const schema = createSettingsMenuSchema();
        const rotationCards = containerDiv.querySelectorAll('.mm-settings-card');

        // Hide rotation card if rotation section node is marked VR-only
        if (rotationCards[1]) {
            const rotationSectionNode = schema.nodes.find((n: any) => n.text === SETTINGS_MENU_LABELS.rotationSection);
            if (rotationSectionNode && !isNodeVisibleOnPlatform(rotationSectionNode, 'web')) {
                (rotationCards[1] as HTMLElement).style.display = 'none';
            }
        }
    }

    private updatePersistedSettingsDebugView(): void {
        const debugText = document.getElementById('mm-settings-debug-text') as HTMLTextAreaElement | null;
        if (!debugText) {
            return;
        }

        const lines = UserSettingsStore.getKnownPersistedSettings().map((entry) => {
            const value = entry.value === '' ? '<empty>' : entry.value;
            return `[${entry.storage}] ${entry.key} = ${value}`;
        });

        debugText.value = lines.join('\n');
    }

    private copyPersistedSettingsToClipboard(): void {
        const debugText = document.getElementById('mm-settings-debug-text') as HTMLTextAreaElement | null;
        if (!debugText) {
            return;
        }

        const textToCopy = debugText.value ?? '';
        if (!textToCopy) {
            return;
        }

        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            navigator.clipboard.writeText(textToCopy).catch(() => {
                debugText.select();
                document.execCommand('copy');
                debugText.setSelectionRange(0, 0);
            });
            return;
        }

        debugText.select();
        document.execCommand('copy');
        debugText.setSelectionRange(0, 0);
    }

    updateConditionals(): void {
        const modeSelect = document.getElementById('mm-settings-rotation-mode') as HTMLSelectElement | null;
        const smoothRow = document.getElementById('mm-settings-smooth-row') as HTMLDivElement | null;
        const snapRow = document.getElementById('mm-settings-snap-row') as HTMLDivElement | null;

        if (!modeSelect || !smoothRow || !snapRow) {
            return;
        }

        const pair = this.findRotationPair();
        if (!pair) {
            modeSelect.disabled = true;
            smoothRow.style.display = 'none';
            snapRow.style.display = 'none';
            return;
        }

        modeSelect.disabled = false;
        this.currentMode = this.syncModeFromComponents(pair);
        modeSelect.value = this.currentMode;

        smoothRow.style.display = this.currentMode === 'smooth' ? 'flex' : 'none';
        snapRow.style.display = this.currentMode === 'snap' ? 'flex' : 'none';
        this.updatePersistedSettingsDebugView();
    }

    displayMenuContent(containerDiv: HTMLDivElement): void {
        containerDiv.innerHTML = MM_SETTINGS_HTML;
        this.applySchemaLabels(containerDiv);
        this.hideVrOnlyElements(containerDiv);

        const modeSelect = document.getElementById('mm-settings-rotation-mode') as HTMLSelectElement | null;
        const smoothSlider = document.getElementById('mm-settings-smooth-slider') as HTMLInputElement | null;
        const snapSlider = document.getElementById('mm-settings-snap-slider') as HTMLInputElement | null;
        const debugCopyButton = document.getElementById('mm-settings-debug-copy') as HTMLButtonElement | null;
        const debugRefreshButton = document.getElementById('mm-settings-debug-refresh') as HTMLButtonElement | null;

        if (!modeSelect || !smoothSlider || !snapSlider || !debugCopyButton || !debugRefreshButton) {
            Logger.error(MODULE, 'Could not find settings submenu controls.');
            return;
        }

        this.wireAudioSettingsControls();

        const pair = this.findRotationPair();
        if (pair) {
            this.currentMode = UserSettingsStore.getRotationMode('snap');
            modeSelect.value = this.currentMode;

            const persistedSmoothSpeed = UserSettingsStore.getNumber(UserSettingsStore.Keys.SmoothRotationSpeed);
            const smoothSpeed = persistedSmoothSpeed ?? pair.smooth.getRotationSpeed();
            pair.smooth.setRotationSpeed(smoothSpeed);
            smoothSlider.value = String(smoothSpeed);
            this.setSmoothValueLabel(smoothSpeed);

            const persistedSnapAngle = UserSettingsStore.getNumber(UserSettingsStore.Keys.SnapRotationAngle);
            const snapAngle = persistedSnapAngle ?? pair.snap.getSnapAngle();
            pair.snap.setSnapAngle(snapAngle);
            snapSlider.value = String(snapAngle);
            this.setSnapValueLabel(snapAngle);

            this.applyMode(this.currentMode);
        }

        modeSelect.addEventListener('change', () => {
            const mode = modeSelect.value === 'snap' ? 'snap' : 'smooth';
            this.applyMode(mode);
        });

        smoothSlider.addEventListener('input', () => {
            const pairNow = this.findRotationPair();
            if (!pairNow) {
                return;
            }
            const value = Number(smoothSlider.value);
            pairNow.smooth.setRotationSpeed(value);
            UserSettingsStore.setNumber(UserSettingsStore.Keys.SmoothRotationSpeed, value);
            this.setSmoothValueLabel(value);
        });

        snapSlider.addEventListener('input', () => {
            const pairNow = this.findRotationPair();
            if (!pairNow) {
                return;
            }
            const value = Number(snapSlider.value);
            pairNow.snap.setSnapAngle(value);
            UserSettingsStore.setNumber(UserSettingsStore.Keys.SnapRotationAngle, value);
            this.setSnapValueLabel(value);
        });

        debugRefreshButton.addEventListener('click', () => {
            this.updatePersistedSettingsDebugView();
        });

        debugCopyButton.addEventListener('click', () => {
            this.copyPersistedSettingsToClipboard();
        });

        this.updateConditionals();
        this.updatePersistedSettingsDebugView();
    }

    hideContent(): void {
        const modeSelect = document.getElementById('mm-settings-rotation-mode');
        const smoothSlider = document.getElementById('mm-settings-smooth-slider');
        const snapSlider = document.getElementById('mm-settings-snap-slider');
        const audioRoutingMode = document.getElementById('mm-settings-audio-routing-mode');
        const contentHintMode = document.getElementById('mm-settings-audio-content-hint');
        const noiseMode = document.getElementById('mm-settings-audio-noise-mode');
        const speechBitrate = document.getElementById('mm-settings-audio-speech-bitrate');
        const musicBitrate = document.getElementById('mm-settings-audio-music-bitrate');
        const echoCancellation = document.getElementById('mm-settings-audio-echo-cancellation');
        const noiseSuppression = document.getElementById('mm-settings-audio-noise-suppression');
        const broadcastDisable = document.getElementById('mm-settings-audio-broadcast-disable');
        const autoGain = document.getElementById('mm-settings-audio-auto-gain');
        const bitrateCap = document.getElementById('mm-settings-audio-bitrate-cap');
        const debugCopyButton = document.getElementById('mm-settings-debug-copy');
        const debugRefreshButton = document.getElementById('mm-settings-debug-refresh');

        let clone = modeSelect?.cloneNode(true);
        if (modeSelect && clone) {
            modeSelect.parentNode?.replaceChild(clone, modeSelect);
        }

        clone = smoothSlider?.cloneNode(true);
        if (smoothSlider && clone) {
            smoothSlider.parentNode?.replaceChild(clone, smoothSlider);
        }

        clone = snapSlider?.cloneNode(true);
        if (snapSlider && clone) {
            snapSlider.parentNode?.replaceChild(clone, snapSlider);
        }

        clone = debugRefreshButton?.cloneNode(true);
        if (debugRefreshButton && clone) {
            debugRefreshButton.parentNode?.replaceChild(clone, debugRefreshButton);
        }

        clone = debugCopyButton?.cloneNode(true);
        if (debugCopyButton && clone) {
            debugCopyButton.parentNode?.replaceChild(clone, debugCopyButton);
        }

        clone = audioRoutingMode?.cloneNode(true);
        if (audioRoutingMode && clone) {
            audioRoutingMode.parentNode?.replaceChild(clone, audioRoutingMode);
        }

        clone = contentHintMode?.cloneNode(true);
        if (contentHintMode && clone) {
            contentHintMode.parentNode?.replaceChild(clone, contentHintMode);
        }

        clone = noiseMode?.cloneNode(true);
        if (noiseMode && clone) {
            noiseMode.parentNode?.replaceChild(clone, noiseMode);
        }

        clone = speechBitrate?.cloneNode(true);
        if (speechBitrate && clone) {
            speechBitrate.parentNode?.replaceChild(clone, speechBitrate);
        }

        clone = musicBitrate?.cloneNode(true);
        if (musicBitrate && clone) {
            musicBitrate.parentNode?.replaceChild(clone, musicBitrate);
        }

        clone = echoCancellation?.cloneNode(true);
        if (echoCancellation && clone) {
            echoCancellation.parentNode?.replaceChild(clone, echoCancellation);
        }

        clone = noiseSuppression?.cloneNode(true);
        if (noiseSuppression && clone) {
            noiseSuppression.parentNode?.replaceChild(clone, noiseSuppression);
        }

        clone = broadcastDisable?.cloneNode(true);
        if (broadcastDisable && clone) {
            broadcastDisable.parentNode?.replaceChild(clone, broadcastDisable);
        }

        clone = autoGain?.cloneNode(true);
        if (autoGain && clone) {
            autoGain.parentNode?.replaceChild(clone, autoGain);
        }

        clone = bitrateCap?.cloneNode(true);
        if (bitrateCap && clone) {
            bitrateCap.parentNode?.replaceChild(clone, bitrateCap);
        }
    }
}
